import { useEffect, useState } from 'react';
import { Sprout, Package } from 'lucide-react';
import { BottomSheet } from '@/components/bottomSheet/bottomSheet';
import { Button } from '@/components/button/button';
import { FilterChip } from '@/components/filterChip/filterChip';
import {
  LocationCascade,
  type LocationSelection,
} from '@/components/locationCascade/locationCascade';
import { isLocationSelected } from '@/components/locationCascade/locationSelection';
import type { ListedWithin, ListingFilters } from '@/services/listingService';
import { packagedFilterLabel } from '@/util/produceStatus';
import { tr } from '@/i18n/tr';
import type { ProduceStatus } from '@/types/domain';
import styles from './filterSheet.module.scss';

/**
 * What the drawer edits — category, query and producer live in the header.
 *
 * Location is carried as the whole cascade selection rather than as the two or
 * three fields the listing query needs, because the callers need more than the
 * query does: the map frames the selection's extent and draws the outline of
 * its deepest level, and neither is reachable from a place name alone.
 * `undefined` means "anywhere", so counting the defined values of this object
 * still counts the active filters.
 */
export type SheetFilters = Pick<
  ListingFilters,
  'status' | 'minPrice' | 'maxPrice' | 'listedWithin'
> & {
  location?: LocationSelection;
};

const LISTED_WITHIN: ListedWithin[] = ['1d', '7d', '30d'];

export interface FilterSheetProps {
  open: boolean;
  value: SheetFilters;
  onApply: (next: SheetFilters) => void;
  onClose: () => void;
}

// Location, status, price range and listing date.
export function FilterSheet({ open, value, onApply, onClose }: FilterSheetProps) {
  const [draft, setDraft] = useState<SheetFilters>(value);

  // re-seed the draft each time the sheet opens; edits stay local until Apply
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const setStatus = (status?: ProduceStatus) => setDraft((d) => ({ ...d, status }));
  const toPrice = (raw: string) => (raw === '' ? undefined : Number(raw));

  // An emptied cascade reports a selection object whose fields are all unset,
  // which would still read as an active filter. Store that as absent instead.
  const setLocation = (next: LocationSelection) =>
    setDraft((d) => ({ ...d, location: isLocationSelected(next) ? next : undefined }));

  return (
    <BottomSheet
      open={open}
      title={tr.explore.filters.title}
      onClose={onClose}
      footer={
        <>
          {/* Applies straight away — Temizle reads as an action, not a form reset. */}
          <Button
            variant="ghost"
            onClick={() => {
              setDraft({});
              onApply({});
              onClose();
            }}
          >
            {tr.explore.filters.clear}
          </Button>
          <Button
            fullWidth
            onClick={() => {
              onApply(draft);
              onClose();
            }}
          >
            {tr.explore.filters.apply}
          </Button>
        </>
      }
    >
      <section>
        <h3 className={styles.label}>{tr.listing.location}</h3>
        {/* Browsing a whole province, or a whole district, has to stay possible:
            here the mahalle level narrows the result, it does not gate it. */}
        <LocationCascade
          value={draft.location ?? {}}
          onChange={setLocation}
          requireNeighbourhood={false}
        />
      </section>

      <section>
        <h3 className={styles.label}>{tr.listing.condition}</h3>
        <div className={styles.chips}>
          <FilterChip selected={!draft.status} onClick={() => setStatus(undefined)}>
            {tr.common.all}
          </FilterChip>
          <FilterChip
            icon={<Sprout />}
            selected={draft.status === 'onBranch'}
            onClick={() => setStatus('onBranch')}
          >
            {tr.listing.produceStatus.onBranch}
          </FilterChip>
          <FilterChip
            icon={<Package />}
            selected={draft.status === 'inCrate'}
            onClick={() => setStatus('inCrate')}
          >
            {/* No category in scope here — the filter spans all of them, so it
                names every word the packaged option can carry. */}
            {packagedFilterLabel()}
          </FilterChip>
        </div>
      </section>

      <section>
        <h3 className={styles.label}>{tr.explore.filters.priceRange}</h3>
        <div className={styles.row}>
          <input
            type="number"
            min={0}
            inputMode="decimal"
            placeholder={tr.explore.filters.minPrice}
            value={draft.minPrice ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, minPrice: toPrice(e.target.value) }))}
          />
          <span className={styles.dash} aria-hidden>
            –
          </span>
          <input
            type="number"
            min={0}
            inputMode="decimal"
            placeholder={tr.explore.filters.maxPrice}
            value={draft.maxPrice ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, maxPrice: toPrice(e.target.value) }))}
          />
        </div>
      </section>

      <section>
        <h3 className={styles.label}>{tr.explore.filters.listedWithin}</h3>
        <div className={styles.chips}>
          <FilterChip
            selected={!draft.listedWithin}
            onClick={() => setDraft((d) => ({ ...d, listedWithin: undefined }))}
          >
            {tr.common.all}
          </FilterChip>
          {LISTED_WITHIN.map((w) => (
            <FilterChip
              key={w}
              selected={draft.listedWithin === w}
              onClick={() => setDraft((d) => ({ ...d, listedWithin: w }))}
            >
              {tr.explore.filters.listedWithinOptions[w]}
            </FilterChip>
          ))}
        </div>
      </section>
    </BottomSheet>
  );
}
